import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PaymentForm from './PaymentForm';
import paymentService, { Payment, PaymentFilters } from '../../../../services/paymentService';
import apiService from '../../../../services/apiService';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

const PaymentManagementNew: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [accounts, setAccounts] = useState<Account[]>([]);

  React.useEffect(() => {
    loadPayments();
    loadAccounts();
  }, [filters]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getAllPayments(filters);
      setPayments(data);
    } catch (error: any) {
      message.error(`Failed to load payments: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      setAccounts(response.data || []);
    } catch (error: any) {
      console.error('Failed to load accounts:', error);
    }
  };

  const handleCreate = () => {
    setSelectedPayment(null);
    setFormVisible(true);
  };

  const handleView = (payment: Payment) => {
    setSelectedPayment(payment);
    setViewModalVisible(true);
  };

  const handleEdit = (payment: Payment) => {
    setSelectedPayment(payment);
    setFormVisible(true);
  };

  const handleDelete = async (payment: Payment) => {
    Modal.confirm({
      title: 'Delete Payment',
      content: `Are you sure you want to delete payment ${payment.paymentNo}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await paymentService.deletePayment(payment.id);
          message.success('Payment deleted successfully');
          loadPayments();
        } catch (error: any) {
          message.error(`Failed to delete payment: ${error.message}`);
        }
      }
    });
  };

  const handlePost = async (payment: Payment) => {
    try {
      // Find required accounts
      const cashAccount = accounts.find(a => a.code?.includes('1000'));  // Cash/Bank
      const arAccount = accounts.find(a => a.code?.includes('1200'));  // Accounts Receivable
      const apAccount = accounts.find(a => a.code?.includes('2000'));  // Accounts Payable

      if (!cashAccount || !arAccount || !apAccount) {
        message.error('Missing required accounts. Please ensure Cash (1000), AR (1200), and AP (2000) exist.');
        return;
      }

      await paymentService.postPayment(payment.id, {
        cashAccountId: cashAccount.id,
        arAccountId: arAccount.id,
        apAccountId: apAccount.id,
        journalId: payment.journalId
      });

      message.success('Payment posted successfully');
      loadPayments();
    } catch (error: any) {
      message.error(`Failed to post payment: ${error.message}`);
    }
  };

  const handleCancel = async (payment: Payment) => {
    // Find required accounts by code (same as handlePost)
    const cashAccount = accounts.find(a => a.code?.includes('1000'));  // Cash/Bank
    const arAccount = accounts.find(a => a.code?.includes('1200'));  // Accounts Receivable
    const apAccount = accounts.find(a => a.code?.includes('2000'));  // Accounts Payable

    if (!cashAccount || !arAccount || !apAccount) {
      message.error('Missing required accounts. Please ensure Cash (1000), AR (1200), and AP (2000) exist.');
      return;
    }

    Modal.confirm({
      title: 'Cancel Payment',
      content: (
        <div>
          <p>This will cancel payment <strong>{payment.paymentNo}</strong> and:</p>
          <ul style={{ marginTop: 12, paddingLeft: 20 }}>
            <li>Create a reversing journal entry</li>
            <li>Change payment status to CANCELLED</li>
            <li>Revert linked invoice(s) to unpaid status</li>
          </ul>
          <p style={{ marginTop: 12, color: '#ff4d4f' }}>
            <strong>Warning:</strong> This action maintains an audit trail but cannot be undone.
          </p>
        </div>
      ),
      okText: 'Cancel Payment',
      okType: 'danger',
      cancelText: 'Keep Payment',
      width: 520,
      onOk: async () => {
        try {
          const postPaymentInput = {
            cashAccountId: cashAccount.id,
            arAccountId: arAccount.id,
            apAccountId: apAccount.id,
            journalId: payment.journalId
          };

          await paymentService.cancelPayment(payment.id, postPaymentInput);

          message.success(`Payment ${payment.paymentNo} has been cancelled`);
          loadPayments();
        } catch (error: any) {
          message.error(`Failed to cancel payment: ${error.message}`);
        }
      }
    });
  };

  const handleFormClose = () => {
    setFormVisible(false);
    setSelectedPayment(null);
  };

  const handleFormSuccess = () => {
    loadPayments();
    setFormVisible(false);
    setSelectedPayment(null);
  };

  const getStatusColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      POSTED: 'success',
      CANCELLED: 'error'
    };
    return colors[state] || 'default';
  };

  const getStatusIcon = (state: string) => {
    const icons: Record<string, React.ReactNode> = {
      DRAFT: <ClockCircleOutlined />,
      POSTED: <CheckCircleOutlined />,
      CANCELLED: <CloseCircleOutlined />
    };
    return icons[state] || null;
  };

  const columns: ColumnsType<Payment> = [
    {
      title: 'Payment No',
      dataIndex: 'paymentNo',
      key: 'paymentNo',
      width: 150,
      fixed: 'left',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 100,
      render: (type) => (
        <Tag color={type === 'INBOUND' ? 'green' : 'orange'}>
          {type}
        </Tag>
      )
    },
    {
      title: 'Partner',
      dataIndex: ['partner', 'name'],
      key: 'partner',
      width: 180,
      render: (text, record) => text || record.partnerId
    },
    {
      title: 'Invoice No',
      key: 'invoiceNo',
      width: 150,
      render: (_, record) => {
        if (record.invoiceAllocations && record.invoiceAllocations.length > 0) {
          const invoiceNos = record.invoiceAllocations
            .map((alloc: any) => alloc.invoice?.invoiceNo)
            .filter(Boolean)
            .join(', ');
          return invoiceNos || '-';
        }
        return '-';
      }
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount) => (
        <span style={{ fontWeight: 500 }}>
          ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      )
    },
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Account',
      key: 'account',
      width: 180,
      render: (_, record) => {
        if (record.account) {
          return `${record.account.code} - ${record.account.name}`;
        }
        return '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state) => (
        <Tag color={getStatusColor(state)} icon={getStatusIcon(state)}>
          {state}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          {record.state === 'DRAFT' && (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handlePost(record)}
            >
              Post
            </Button>
          )}
          {record.state === 'DRAFT' && (
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {record.state === 'DRAFT' && (
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
          {record.state === 'POSTED' && (
            <Button
              type="default"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancel(record)}
            >
              Cancel
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle" wrap>
              <Select
                placeholder="Payment Type"
                style={{ width: 150 }}
                allowClear
                onChange={(value) => setFilters({ ...filters, paymentType: value })}
              >
                <Option value="INBOUND">Inbound</Option>
                <Option value="OUTBOUND">Outbound</Option>
              </Select>
              <Select
                placeholder="Status"
                style={{ width: 150 }}
                allowClear
                onChange={(value) => setFilters({ ...filters, state: value })}
              >
                <Option value="DRAFT">Draft</Option>
                <Option value="POSTED">Posted</Option>
                <Option value="CANCELLED">Cancelled</Option>
              </Select>
              <RangePicker
                onChange={(dates) => {
                  if (dates) {
                    setFilters({
                      ...filters,
                      fromDate: dates[0]?.format('YYYY-MM-DD'),
                      toDate: dates[1]?.format('YYYY-MM-DD')
                    });
                  } else {
                    setFilters({ ...filters, fromDate: undefined, toDate: undefined });
                  }
                }}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Create Payment
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Payments Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} payments`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <PaymentForm
        visible={formVisible}
        payment={selectedPayment}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />

      <Modal
        title="Payment Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedPayment && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Payment No">
              <strong>{selectedPayment.paymentNo}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedPayment.state)} icon={getStatusIcon(selectedPayment.state)}>
                {selectedPayment.state}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={selectedPayment.paymentType === 'INBOUND' ? 'green' : 'orange'}>
                {selectedPayment.paymentType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Partner Type">
              {selectedPayment.partnerType}
            </Descriptions.Item>
            <Descriptions.Item label="Partner">
              {selectedPayment.partner?.name || selectedPayment.partnerId}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice No(s)">
              {selectedPayment.invoiceAllocations && selectedPayment.invoiceAllocations.length > 0
                ? selectedPayment.invoiceAllocations
                    .map((alloc: any) => alloc.invoice?.invoiceNo)
                    .filter(Boolean)
                    .join(', ') || '-'
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <strong style={{ fontSize: 16 }}>
                ${Number(selectedPayment.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Date">
              {dayjs(selectedPayment.paymentDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="Journal">
              {selectedPayment.journal?.name || selectedPayment.journalId}
            </Descriptions.Item>
            <Descriptions.Item label="Destination Account">
              {selectedPayment.account
                ? `${selectedPayment.account.code} - ${selectedPayment.account.name}`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Memo" span={2}>
              {selectedPayment.memo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created Date" span={2}>
              {dayjs(selectedPayment.createdAt).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PaymentManagementNew;